// Scroll-driven 3D banner — vanilla port of ../../scroll/
// EightModelSlideInScrollCompressed.tsx. The R3F version uses drei, postprocessing
// and remotion's interpolate/easing; here those are reimplemented inline so the
// Shopify section ships without a build step.
//
// SOURCE OF TRUTH. This file lives in nf_remotion/office-space/hero-banner/src/
// and is copied verbatim into nf_shopify_dawn_theme/assets/model-3d-banner.js by
// `npm run sync:hero-banner:shopify`. Edit it here, never in the theme.
//
// three.js is loaded via dynamic import() only after the element decides it will
// actually render (WebGL2 available, no reduced-motion, no mobile override), so
// fallback users never download it, and only once the still has loaded and gone
// idle or the visitor first interacts (START_* below). It ships as a self-hosted single-file bundle
// (../vendor/three-bundle.min.js, built once with esbuild from three@0.160 — see
// ../README.md) so the hero has no third-party origin on its critical path; the
// bundle URL arrives via the data-three-bundle attribute.
//
// All GLBs are Meshopt-compressed (gltfpack) — PMETER was re-encoded from
// Draco so the theme no longer pulls the Draco decoder from gstatic. The
// single shared GLTF cache keeps each model parsed once even though the Scene
// is cloned per slot/knob. The deployed asset names are hero-banner-*.glb so the
// theme's flat assets/ directory cannot alias them to the product viewer's
// higher-poly POE-*-screen-textured.glb copies.

// ── timing / layout constants (mirror EightModelSlideInScrollCompressed) ─────
const deg = (d) => (d * Math.PI) / 180;

const RIG_CENTER = [0.28, 0, 0.03];
const FLOOR_Z = -0.035;

const SLOTS = [
  { external: [0.147, -0.138, 0.052], internal: [0.147, 0, 0.052] },
  { external: [0.222, -0.138, 0.052], internal: [0.222, 0, 0.052] },
  { external: [0.297, -0.138, 0.052], internal: [0.297, 0, 0.052] },
  { external: [0.372, -0.138, 0.052], internal: [0.372, 0, 0.052] },
  { external: [0.147, -0.138, 0.006], internal: [0.147, 0, 0.006] },
  { external: [0.222, -0.138, 0.006], internal: [0.222, 0, 0.006] },
  { external: [0.297, -0.138, 0.006], internal: [0.297, 0, 0.006] },
  { external: [0.372, -0.138, 0.006], internal: [0.372, 0, 0.006] },
];

const KNOBS = [
  { position: [0.158, 0, 0.046], startRotation: [deg(90), 0, deg(-90)], endRotation: [deg(90), 0, 0] },
  { position: [0.233, 0, 0.046], startRotation: [deg(90), 0, deg(-90)], endRotation: [deg(90), 0, 0] },
  { position: [0.308, 0, 0.046], startRotation: [deg(90), 0, deg(-90)], endRotation: [deg(90), 0, 0] },
  { position: [0.383, 0, 0.046], startRotation: [deg(90), 0, deg(-90)], endRotation: [deg(90), 0, 0] },
  { position: [0.190, 0, 0.046], startRotation: [deg(90), 0, deg(90)], endRotation: [deg(90), 0, deg(180)] },
  { position: [0.265, 0, 0.046], startRotation: [deg(90), 0, deg(90)], endRotation: [deg(90), 0, deg(180)] },
  { position: [0.340, 0, 0.046], startRotation: [deg(90), 0, deg(90)], endRotation: [deg(90), 0, deg(180)] },
  { position: [0.415, 0, 0.046], startRotation: [deg(90), 0, deg(90)], endRotation: [deg(90), 0, deg(180)] },
];

const MODULE_LABELS = [
  'POE-SPEC-AN',
  'POE-ATTEN-6G',
  'POE-SPDT-6G',
  'POE-SP4T-6G',
  'POE-SIGGEN',
  'POE-LNA-6G',
  'POE-PMETER-6G',
  'POE-WLK-6G',
];

const MODULE_DETAILS = [
  'Spectrum Analyzer, 9 kHz – 6 GHz, real-time IQ capture',
  // ATTEN, SPDT and SP4T quote the published spec tables (custom.rf_specifications
  // in the theme's content/products/*/product.yml); keep them in step.
  '100 kHz – 6 GHz, 0 – 95.25 dB, 0.25 dB resolution',
  '100 kHz – 6 GHz, 2-way, 40 dB minimum isolation',
  '100 kHz – 6 GHz, 4-way, 32 dB minimum isolation',
  '9 kHz – 6 GHz, −90 to +10 dBm',
  '0.5 – 6 GHz, +20 dB gain',
  '0 – 6 GHz, −60 to 0 dBm, ±0.5 dB',
  '0.5 – 6 GHz, 2-way, 30 dB isolation',
];

const SLOT_INSERTION_ORDER = [0, 4, 1, 5, 2, 6, 3, 7];

// Camera path: hold at MIDDLE until the last slot finishes inserting + the
// final knob finishes rotating, then lerp to ENDING. We stay in perspective
// projection the entire way — the previous perspective→orthographic switch at
// 100% scroll produced a visible projection snap.
const MIDDLE_CAMERA = { position: [0.38, -0.25, 0.2], rotation: [deg(60), deg(20), 0] };
const ENDING_CAMERA = { position: [0.24, -0.28, 0.055], rotation: [deg(82), 0, 0] };

const FOV = 50;
// The composition was framed for a 2:1 viewport. On narrower aspects we widen
// the vertical FOV to keep the same horizontal coverage, clamped to MAX_FOV to
// avoid fisheye distortion; whatever coverage the clamp gives up is recovered
// by dollying the camera back along its view axis.
const BASE_ASPECT = 2;
const MAX_FOV = 68;
// Distance along the view axis at which the dolly-back (tall frames, once
// MAX_FOV is hit) preserves the 2:1 composition's scale exactly. It is the
// seated rack's distance from the ENDING camera, so on phones the first
// live frame matches the fallback still — which is rendered at 2:1 with no
// dolly — pixel for pixel; at 0.3 the rack came in ~5 % smaller than the
// still and the hand-off breathed.
const CAMERA_TARGET_DIST = 0.275;
// Lens shift. The 2:1 composition leaves the top ~22 % of the frame empty
// above the rack, which under a 140 px site header read as dead space. The
// visible window is moved up by VIEW_SHIFT_UP × frameHeight / 2 with an
// off-axis projection (PerspectiveCamera.setViewOffset), so the rack sits
// just under the frame's top edge while the perspective stays exactly the
// one the scene was composed for — a camera tilt would have changed it.
// 0.36 puts the rack's top edge ~4 % of the frame height below the top.
//
// Frames wider than 2:1 (the CSS caps the frame's height to leave a band for
// the heading and signup below it) crop the *bottom* of that shifted 2:1
// composition instead of shrinking it, so the rack keeps its size and the
// modules simply rise into view from the frame's lower edge.
const VIEW_SHIFT_UP = 0.36;
const BASE_FPS = 30;
const INTRO_SECONDS = 0;
const SLIDE_SECONDS = 0.8;
const SLOT_INTERVAL_SECONDS = 1;
const FADE_SECONDS = 0.25;
const HOLD_SECONDS = 0.5;
const KNOB_ROTATION_SECONDS = 0.2;
const CAMERA_TRANSITION_SECONDS = 1;
const POST_INSERTION_SECONDS = KNOB_ROTATION_SECONDS + CAMERA_TRANSITION_SECONDS;

const TOTAL_SECONDS =
  INTRO_SECONDS +
  FADE_SECONDS +
  HOLD_SECONDS +
  (SLOTS.length - 1) * SLOT_INTERVAL_SECONDS +
  SLIDE_SECONDS +
  POST_INSERTION_SECONDS;
const TOTAL_FRAMES = Math.round(TOTAL_SECONDS * BASE_FPS);

// ── reveal: hand-off from the fallback still to the live scene ───────────────
// The still (model-3d-banner-fallback-0.webp) is the animation's resting
// frame: head-on camera, all eight modules seated, knobs turned. It is on
// screen from the first paint while three.js and the GLBs download. The live
// scene must therefore *open* on that same pose, or the visitor watches the
// seated modules vanish and the camera snap sideways the moment the chassis
// arrives — which is exactly what happened when the first live frame was
// scroll frame 0.
//
// So: the canvas stays blank until the slot models are in (the still stands
// in), the first live frame reproduces the resting pose and the still is
// swapped out in that same frame (no fade: see .model-3d-banner__fallback),
// the scene holds that pose for REVEAL_HOLD_SECONDS, then blends over
// REVEAL_SECONDS to whatever pose the scroll position asks for — normally
// frame 0, so the modules fade and eject, the knobs turn back and the camera
// swings to the side view. The blend targets the *live* scroll frame, so a
// visitor who scrolls during the reveal is never snapped; the timeline takes
// over seamlessly the instant the reveal ends. No labels show while it runs.
const REVEAL_HOLD_SECONDS = 0.4; // a beat on the resting pose after the swap
const REVEAL_SECONDS = 1;
const REVEAL_FADE_PORTION = 0.6; // modules are fully gone this far into the reveal
const REVEAL_LOAD_TIMEOUT_SECONDS = 8; // stop waiting for straggling slot GLBs

// ── deferred start ───────────────────────────────────────────────────────────
// The still is the LCP element and already shows the opening frame, so nothing
// visible waits on three.js. The bundle and the ten GLBs (~2 MB) are therefore
// kept off the critical path: the scene starts once the still has loaded and
// the main thread is idle, or at the visitor's first scroll, wheel, touch,
// pointer press or key press if that comes sooner.
// Not the window load event: that also waits for every iframe on the page, and
// third-party ones (Shop Pay's /pay/hop, pixel sandboxes, anything an extension
// injects) can hold it for many seconds, which left the still up for 10 s+
// unless the visitor scrolled.
const START_IDLE_TIMEOUT_MS = 1000; // requestIdleCallback deadline after the still loads
const START_NO_IDLE_DELAY_MS = 300; // Safari has no requestIdleCallback
// [type, capture]. Page scrolling only: a capturing 'scroll' listener would also
// fire for carousels scrolling themselves.
const START_INTENT_EVENTS = [
  ['scroll', false],
  ['wheel', true],
  ['touchstart', true],
  ['pointerdown', true],
  ['keydown', true],
];

const ENDING_LABEL_TITLE = 'PD8X';
const ENDING_LABEL_DETAIL =
  'Rack-mountable 2U backplane with unified power, data, and automatic slot detection';

// ── quality tiers ────────────────────────────────────────────────────────────
// At high display density, native MSAA plus native pixels gives crisp output
// without allocating an HDR/effects chain. At DPR 1, high/medium retain the
// composer (multisampled HalfFloat RT + restrained bloom + ACES, plus SMAA
// on high) to clean subpixel edges. Phones always use native antialiasing.
// The pipeline is chosen at creation because `antialias` is immutable.
// Every tier renders at native device resolution (DPR capped per tier, pixel
// budget as a backstop) — resolution is the most visible quality axis, so
// effects are what scale between tiers, not sharpness. At standard density,
// high adds SMAA for the specular shimmer MSAA can't touch. Runtime demotion drops
// effects first and only reduces resolution as a last resort.
const QUALITY_TIERS = {
  high: { dpr: 2, pixelBudget: 8e6, samples: 4, bloom: true, smaa: true, composer: true },
  medium: { dpr: 2, pixelBudget: 4.5e6, samples: 4, bloom: true, smaa: false, composer: true },
  low: { dpr: 3, pixelBudget: 2.6e6, composer: false },
};

// Demotion ladder: cumulative stages applied when scrolling can't hold frame
// rate. Effects go before resolution.
const DEMOTE_STAGES = ['smaa-off', 'bloom-off', 'dpr-1.5', 'dpr-1'];
const DEMOTE_STORAGE_KEY = 'm3db-demote-v4';

const readStoredDemoteStage = () => {
  try {
    const n = parseInt(sessionStorage.getItem(DEMOTE_STORAGE_KEY), 10);
    return Number.isInteger(n) ? Math.max(0, Math.min(n, DEMOTE_STAGES.length)) : 0;
  } catch (e) {
    return 0;
  }
};

const storeDemoteStage = (n) => {
  try {
    sessionStorage.setItem(DEMOTE_STORAGE_KEY, String(n));
  } catch (e) {
    /* private mode — demotion just won't persist */
  }
};

const pickTier = (el) => {
  const forced = el.dataset.quality;
  if (forced && QUALITY_TIERS[forced]) return forced;
  // Screen size is the primary signal — tablets are large screens and deserve
  // the same crisp output as desktops (pointer type says nothing about GPU
  // strength). Phones use the composer-less tier at full native resolution;
  // weak/small machines start medium; everything else starts high and lets
  // runtime demotion handle stragglers.
  if (window.innerWidth < 750) return 'low';
  const cores = navigator.hardwareConcurrency || 8;
  const area = window.innerWidth * window.innerHeight;
  if (cores <= 4 || area < 720000) return 'medium';
  return 'high';
};

// ── three.js dynamic loading ─────────────────────────────────────────────────
let THREE;
let EffectComposerCtor;
let RenderPassCtor;
let UnrealBloomPassCtor;
let OutputPassCtor;
let SMAAPassCtor;
let sharedLoader;
let gltfCache;
let threePromise = null;

const loadThree = (bundleUrl) => {
  if (!threePromise) {
    threePromise = import(bundleUrl).then((bundle) => {
      THREE = bundle.THREE;
      EffectComposerCtor = bundle.EffectComposer;
      RenderPassCtor = bundle.RenderPass;
      UnrealBloomPassCtor = bundle.UnrealBloomPass;
      OutputPassCtor = bundle.OutputPass;
      SMAAPassCtor = bundle.SMAAPass;
      sharedLoader = new bundle.GLTFLoader().setMeshoptDecoder(bundle.MeshoptDecoder);
      gltfCache = new Map();
    });
  }
  return threePromise;
};

const loadGLB = (url) => {
  if (!gltfCache.has(url)) {
    gltfCache.set(
      url,
      new Promise((resolve, reject) => {
        sharedLoader.load(url, (gltf) => resolve(gltf.scene), undefined, reject);
      }),
    );
  }
  return gltfCache.get(url);
};

// ── easing + interpolate (remotion-compatible) ───────────────────────────────
// Cubic bezier from (0,0)→(1,1) through (x1,y1),(x2,y2). Solve t from x via
// Newton-Raphson + bisection fallback, then evaluate y(t). Matches the curves
// shipped by remotion's Easing.bezier.
const bezierEasing = (x1, y1, x2, y2) => {
  const cx = 3 * x1;
  const bx = 3 * (x2 - x1) - cx;
  const ax = 1 - cx - bx;
  const cy = 3 * y1;
  const by = 3 * (y2 - y1) - cy;
  const ay = 1 - cy - by;
  const sampleX = (t) => ((ax * t + bx) * t + cx) * t;
  const sampleY = (t) => ((ay * t + by) * t + cy) * t;
  const sampleDX = (t) => (3 * ax * t + 2 * bx) * t + cx;
  const solve = (x) => {
    let t = x;
    for (let i = 0; i < 8; i++) {
      const dx = sampleX(t) - x;
      if (Math.abs(dx) < 1e-6) return t;
      const d = sampleDX(t);
      if (Math.abs(d) < 1e-6) break;
      t -= dx / d;
    }
    let lo = 0;
    let hi = 1;
    t = x;
    while (lo < hi) {
      const v = sampleX(t);
      if (Math.abs(v - x) < 1e-6) return t;
      if (v < x) lo = t;
      else hi = t;
      t = (lo + hi) / 2;
    }
    return t;
  };
  return (x) => {
    if (x <= 0) return 0;
    if (x >= 1) return 1;
    return sampleY(solve(x));
  };
};

const easeOutCubic = (t) => 1 - Math.pow(1 - t, 3);
const easeInOutCubic = (t) => (t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2);
const clamp01 = (x) => Math.min(1, Math.max(0, x));
const lerp = (a, b, t) => a + (b - a) * t;
const lerpVec3 = (a, b, t) => [lerp(a[0], b[0], t), lerp(a[1], b[1], t), lerp(a[2], b[2], t)];
const SLIDE_EASING = bezierEasing(0.16, 1, 0.3, 1);
const FADE_EASING = easeOutCubic;
const KNOB_EASING = bezierEasing(0.16, 1, 0.3, 1);

const interpolate = (value, [inMin, inMax], [outMin, outMax], easing = (t) => t) => {
  if (inMax === inMin) return outMin;
  let t = (value - inMin) / (inMax - inMin);
  if (t <= 0) return outMin;
  if (t >= 1) return outMax;
  t = easing(t);
  return outMin + (outMax - outMin) * t;
};

const interpolateVec3 = (frame, fromF, toF, from, to, easing = SLIDE_EASING) => [
  interpolate(frame, [fromF, toF], [from[0], to[0]], easing),
  interpolate(frame, [fromF, toF], [from[1], to[1]], easing),
  interpolate(frame, [fromF, toF], [from[2], to[2]], easing),
];

// ── frame helpers ────────────────────────────────────────────────────────────
const fps = BASE_FPS;
const introFrames = Math.round(INTRO_SECONDS * fps);
const slideFrames = Math.round(SLIDE_SECONDS * fps);
const slotIntervalFrames = Math.round(SLOT_INTERVAL_SECONDS * fps);
const fadeFrames = Math.round(FADE_SECONDS * fps);
const holdFrames = Math.round(HOLD_SECONDS * fps);
const knobRotationFrames = Math.round(KNOB_ROTATION_SECONDS * fps);
const cameraTransitionFrames = Math.round(CAMERA_TRANSITION_SECONDS * fps);

const getSlotSlideStartFrame = (i) => introFrames + fadeFrames + holdFrames + i * slotIntervalFrames;
const getLabelStartFrame = (i) => introFrames + i * slotIntervalFrames;
const finalSlotSlideStartFrame = getSlotSlideStartFrame(SLOT_INSERTION_ORDER.length - 1);
const cameraTransitionStartFrame = finalSlotSlideStartFrame + slideFrames + knobRotationFrames;
const cameraTransitionEndFrame = cameraTransitionStartFrame + cameraTransitionFrames;

// Per-slot material prep — mirrors the prepareMaterial pass in the R3F version.
// Decals are identified by **material name** (Compressed GLBs strip mesh names
// but keep material names like *Decal_face_texture / *Decal_Mat). They're made
// matte (metalness=0, roughness=1, no env reflection) and rendered with
// polygon-offset + depth testing so they sit cleanly on top of the chassis
// without z-fighting or specular shimmer.
const isDecalMaterial = (m) => /decal/i.test(m.name || '');

const prepareClonedSlot = (sourceScene, maxAnisotropy) => {
  const cloned = sourceScene.clone(true);
  const materials = [];
  cloned.traverse((obj) => {
    if (!obj.isMesh || !obj.material) return;
    const inputMats = Array.isArray(obj.material) ? obj.material : [obj.material];
    const apply = (m) => {
      const isDecal = isDecalMaterial(m);
      const c = m.clone();
      c.transparent = true;
      c.depthWrite = !isDecal;
      if (isDecal) {
        c.polygonOffset = true;
        c.polygonOffsetFactor = -4;
        c.polygonOffsetUnits = -4;
        if (c.isMeshStandardMaterial) {
          c.metalness = 0;
          c.roughness = 1;
          c.envMapIntensity = 0;
          if (c.map) {
            c.map.anisotropy = maxAnisotropy;
            c.map.minFilter = THREE.LinearMipmapLinearFilter;
            c.map.magFilter = THREE.LinearFilter;
            c.map.generateMipmaps = true;
            c.map.needsUpdate = true;
          }
        }
      }
      const isScreen =
        !isDecal && c.name.startsWith('mockup_') && c.name.endsWith('_face_texture');
      if (isScreen && c.isMeshStandardMaterial) {
        // Trilinear + max anisotropy, NOT NearestFilter: nearest sampling with
        // no mipmaps looks "sharp" at an exact 1:1 scale but shimmers and
        // pixelates as soon as the camera moves — which here is always.
        const smooth = (tex) => {
          if (!tex) return;
          tex.magFilter = THREE.LinearFilter;
          tex.minFilter = THREE.LinearMipmapLinearFilter;
          tex.generateMipmaps = true;
          tex.anisotropy = maxAnisotropy;
          tex.needsUpdate = true;
        };
        smooth(c.map);
        smooth(c.emissiveMap);
        // The export repeats the UI in base color and emission. Lit diffuse
        // plus a glossy IBL reflection washed that image out during insertion.
        // Let emission carry the UI, with only a restrained glass reflection.
        c.color.setRGB(0, 0, 0);
        c.metalness = 0;
        c.roughness = 0.55;
        c.envMapIntensity = 0.2;
        c.emissiveIntensity = 1.35;
      }
      materials.push(c);
      return c;
    };
    const newMats = inputMats.map(apply);
    obj.material = Array.isArray(obj.material) ? newMats : newMats[0];
    obj.renderOrder = 10;
  });
  return { root: cloned, materials };
};

// ── environment (lightformer-equivalent emissive planes baked to PMREM) ─────
// drei's <Environment><Lightformer/></Environment> renders an emissive scene
// to a cubemap then PMREM-filters it. We replicate that here with plain
// MeshBasicMaterial planes + PMREMGenerator.fromScene.
const buildEnvScene = () => {
  const scene = new THREE.Scene();
  scene.background = new THREE.Color(0x000000);
  const lightformers = [
    { intensity: 1.8, pos: [0, 0, 1.8], scale: [2.4, 1.3], rot: [-Math.PI / 2, 0, 0], color: 0xfff5e6 },
    { intensity: 2.2, pos: [-1.1, -0.4, 0.6], scale: [1.4, 1.0], rot: [0, Math.PI / 2.3, 0], color: 0xffffff },
    { intensity: 2.4, pos: [1.2, 0.7, 0.6], scale: [1.1, 0.85], rot: [0, -Math.PI / 2.3, 0], color: 0xdde6ff },
    { intensity: 1.1, pos: [0, 1.5, 0.5], scale: [2.6, 0.6], rot: [0, Math.PI, 0], color: 0xfff0d8 },
    { intensity: 4.5, pos: [0, -0.1, 0.55], scale: [1.6, 0.04], rot: [-Math.PI / 2, 0, 0], color: 0xffffff },
    { intensity: 2.2, pos: [-0.4, -0.9, 0.25], scale: [0.4, 0.5], rot: [Math.PI / 2, Math.PI / 6, 0], color: 0xffffff },
    { intensity: 2.0, pos: [0.9, -0.9, 0.25], scale: [0.4, 0.5], rot: [Math.PI / 2, -Math.PI / 6, 0], color: 0xffffff },
  ];
  for (const lf of lightformers) {
    const mat = new THREE.MeshBasicMaterial({
      color: lf.color,
      toneMapped: false,
      side: THREE.DoubleSide,
    });
    mat.color.multiplyScalar(lf.intensity);
    const mesh = new THREE.Mesh(new THREE.PlaneGeometry(lf.scale[0], lf.scale[1]), mat);
    mesh.position.set(...lf.pos);
    mesh.rotation.set(...lf.rot);
    scene.add(mesh);
  }
  return scene;
};

// ── contact shadows ──────────────────────────────────────────────────────────
// drei-style <ContactShadows>: render the occluder scene from above with a
// depth-darkness override material, separable Gaussian blur, map the result
// onto a plane sitting just above the floor. The plane is the only "floor":
// the CSS scheme background shows through the transparent canvas, so the soft
// blob grounds the rack without an opaque geometry floor that would clash with
// the page background.
//
// The bake is NOT one-shot (drei's frames={1}). The eight modules slide in from
// the front under scroll, and a shadow frozen at their start position reads as
// a detached grey slab in front of the chassis — invisible on near-black pages,
// glaring on light ones. The texture is re-baked from _renderFrame whenever an
// occluder has moved; at 512² with a trivial shader that is a small fraction of
// the main render.
const SHADOW_BLUR_VERT = `
  varying vec2 vUv;
  void main() {
    vUv = uv;
    gl_Position = vec4(position.xy, 0.0, 1.0);
  }
`;
const SHADOW_BLUR_FRAG = `
  uniform sampler2D tDiffuse;
  uniform vec2 uDir;
  varying vec2 vUv;
  void main() {
    vec4 sum = vec4(0.0);
    sum += texture2D(tDiffuse, vUv + uDir * -4.0) * 0.05;
    sum += texture2D(tDiffuse, vUv + uDir * -3.0) * 0.09;
    sum += texture2D(tDiffuse, vUv + uDir * -2.0) * 0.12;
    sum += texture2D(tDiffuse, vUv + uDir * -1.0) * 0.15;
    sum += texture2D(tDiffuse, vUv) * 0.18;
    sum += texture2D(tDiffuse, vUv + uDir *  1.0) * 0.15;
    sum += texture2D(tDiffuse, vUv + uDir *  2.0) * 0.12;
    sum += texture2D(tDiffuse, vUv + uDir *  3.0) * 0.09;
    sum += texture2D(tDiffuse, vUv + uDir *  4.0) * 0.05;
    gl_FragColor = sum;
  }
`;

const createShadowDepthMaterial = (far, color) => {
  const mat = new THREE.ShaderMaterial({
    transparent: true,
    depthTest: true,
    depthWrite: false,
    side: THREE.DoubleSide,
    uniforms: {
      uColor: { value: color },
      uFar: { value: far },
      uAlpha: { value: 1 },
    },
    vertexShader: `
      varying float vDepth;
      uniform float uFar;
      void main() {
        vec4 mvPosition = modelViewMatrix * vec4(position, 1.0);
        vDepth = -mvPosition.z / uFar;
        gl_Position = projectionMatrix * mvPosition;
      }
    `,
    fragmentShader: `
      uniform vec3 uColor;
      uniform float uAlpha;
      varying float vDepth;
      void main() {
        float d = clamp(vDepth, 0.0, 1.0);
        // Shallow gradient: a steep one prints every module top as a darker
        // blotch once blurred, which reads as a mottled cloud on light pages.
        float alpha = (1.0 - 0.4 * d) * uAlpha;
        if (alpha <= 0.0) discard;
        gl_FragColor = vec4(uColor, alpha);
      }
    `,
  });
  // Each module fades in at its start position before it slides. As a scene
  // override this material knows nothing about that, and would print a
  // full-strength shadow under a module that is still invisible. Read the
  // occluder's own opacity per draw instead; the chassis, knobs and screen
  // stay at 1.
  mat.onBeforeRender = (renderer, scene, camera, geometry, object) => {
    const own = Array.isArray(object.material) ? object.material[0] : object.material;
    const alpha = own && typeof own.opacity === 'number' ? own.opacity : 1;
    if (alpha !== mat.uniforms.uAlpha.value) {
      mat.uniforms.uAlpha.value = alpha;
      mat.uniformsNeedUpdate = true;
    }
  };
  return mat;
};

class ContactShadows {
  constructor(renderer, options) {
    const { position, scale, depth = scale, far, blur, opacity, resolution, color } = options;
    this._renderer = renderer;
    // Several narrower passes converge on a true Gaussian; a single 9-tap pass
    // leaves stair-stepped bands that the low camera angle stretches into
    // stripes — invisible on near-black pages, glaring on light ones.
    this._blurStep = blur / resolution / Math.sqrt(SHADOW_BLUR_PASSES);

    const rtOptions = { format: THREE.RGBAFormat, type: THREE.UnsignedByteType };
    this._rt = new THREE.WebGLRenderTarget(resolution, resolution, rtOptions);
    this._rt.texture.generateMipmaps = false;
    this._rtBlur = new THREE.WebGLRenderTarget(resolution, resolution, rtOptions);
    this._rtBlur.texture.generateMipmaps = false;

    // Z-up scene: shadow camera sits above the floor and looks straight down.
    this._camera = new THREE.OrthographicCamera(-scale / 2, scale / 2, depth / 2, -depth / 2, 0, far);
    this._camera.up.set(0, 1, 0);
    this._camera.position.set(position[0], position[1], position[2] + far);
    this._camera.lookAt(position[0], position[1], position[2]);

    this._depthMat = createShadowDepthMaterial(far, new THREE.Color(color));

    // Separable Gaussian blur, h then v, ping-ponging between the two targets.
    this._blurMat = new THREE.ShaderMaterial({
      uniforms: {
        tDiffuse: { value: this._rt.texture },
        uDir: { value: new THREE.Vector2(this._blurStep, 0) },
      },
      vertexShader: SHADOW_BLUR_VERT,
      fragmentShader: SHADOW_BLUR_FRAG,
    });
    this._fsCam = new THREE.OrthographicCamera(-1, 1, 1, -1, 0, 1);
    this._fsQuad = new THREE.Mesh(new THREE.PlaneGeometry(2, 2), this._blurMat);
    this._fsScene = new THREE.Scene();
    this._fsScene.add(this._fsQuad);

    // The page background shows through the canvas, so the plane must fade to
    // fully transparent well inside its own bounds — otherwise its edges read
    // as a grey trapezoid on light schemes.
    this._planeMat = new THREE.ShaderMaterial({
      transparent: true,
      depthWrite: false,
      side: THREE.DoubleSide,
      polygonOffset: true,
      polygonOffsetFactor: -10,
      polygonOffsetUnits: -10,
      uniforms: {
        tShadow: { value: this._rt.texture },
        uOpacity: { value: opacity },
        uEdgeFade: { value: SHADOW_EDGE_FADE },
      },
      vertexShader: `
        varying vec2 vUv;
        void main() {
          vUv = uv;
          gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
        }
      `,
      fragmentShader: `
        uniform sampler2D tShadow;
        uniform float uOpacity;
        uniform float uEdgeFade;
        varying vec2 vUv;
        void main() {
          vec4 shadow = texture2D(tShadow, vUv);
          vec2 edge = smoothstep(0.0, uEdgeFade, vUv) * smoothstep(0.0, uEdgeFade, 1.0 - vUv);
          float alpha = shadow.a * uOpacity * edge.x * edge.y;
          if (alpha <= 0.002) discard;
          gl_FragColor = vec4(shadow.rgb, alpha);
        }
      `,
    });
    this._plane = new THREE.Mesh(new THREE.PlaneGeometry(scale, depth), this._planeMat);
    this._plane.position.set(position[0], position[1], position[2]);
    // No rotation. The shadow camera sits on +Z looking down with up = +Y, so
    // the texture's u runs along world +X and v along world +Y, exactly how
    // PlaneGeometry maps its UVs. The drei original flips the plane because
    // its Y-up camera renders from *below*; carrying that flip into this Z-up
    // port mirrored every module's shadow onto the opposite column.
    this.group = new THREE.Group();
    this.group.add(this._plane);

    this._prevClearColor = new THREE.Color();
  }

  // Re-render the shadow texture from the occluders' current transforms.
  bake(scene) {
    const renderer = this._renderer;
    const prevOverride = scene.overrideMaterial;
    const prevBackground = scene.background;
    renderer.getClearColor(this._prevClearColor);
    const prevClearAlpha = renderer.getClearAlpha();
    const prevTarget = renderer.getRenderTarget();

    // The plane lives in the same scene; it must not shadow itself.
    this._plane.visible = false;
    scene.background = null;
    scene.overrideMaterial = this._depthMat;
    renderer.setClearColor(0x000000, 0);
    renderer.setRenderTarget(this._rt);
    renderer.clear();
    renderer.render(scene, this._camera);
    scene.overrideMaterial = prevOverride;
    scene.background = prevBackground;
    this._plane.visible = true;

    const blur = this._blurMat.uniforms;
    for (let pass = 0; pass < SHADOW_BLUR_PASSES; pass++) {
      blur.tDiffuse.value = this._rt.texture;
      blur.uDir.value.set(this._blurStep, 0);
      renderer.setRenderTarget(this._rtBlur);
      renderer.clear();
      renderer.render(this._fsScene, this._fsCam);

      blur.tDiffuse.value = this._rtBlur.texture;
      blur.uDir.value.set(0, this._blurStep);
      renderer.setRenderTarget(this._rt);
      renderer.clear();
      renderer.render(this._fsScene, this._fsCam);
    }

    renderer.setRenderTarget(prevTarget);
    renderer.setClearColor(this._prevClearColor, prevClearAlpha);
  }

  dispose() {
    this._rt.dispose();
    this._rtBlur.dispose();
    this._depthMat.dispose();
    this._blurMat.dispose();
    this._fsQuad.geometry.dispose();
    this._plane.geometry.dispose();
    this._planeMat.dispose();
  }
}

// Blur passes and the UV-space width of the plane's edge fade (see
// ContactShadows). Both exist for the transparent canvas: on a light page the
// shadow is the only thing between the rack and the background.
const SHADOW_BLUR_PASSES = 3;
const SHADOW_EDGE_FADE = 0.45;

const CONTACT_SHADOW_OPTIONS = {
  position: [RIG_CENTER[0], 0, FLOOR_Z + 0.0005],
  // The camera sits low, so the floor plane is stretched across the lower
  // frame: keep the plane tight and the blur modest or the shadow becomes a
  // fog behind the headline on light schemes.
  scale: 1.4,
  // Shallow in depth (the viewer looks along the depth axis) so the shadow
  // stays under the chassis instead of spreading toward the camera.
  depth: 0.4,
  far: 0.4,
  blur: 2.4,
  // 0.72 was tuned against a near-black page; on a light page the same shadow
  // reads as a dark slab.
  opacity: 0.25,
  // Heavily blurred anyway — 512 is indistinguishable from 1024 at a quarter
  // of the bake cost.
  resolution: 512,
  color: '#000000',
};

// ── helpers ──────────────────────────────────────────────────────────────────
const setVec3Tuple = (obj3d, tuple) => obj3d.position.set(tuple[0], tuple[1], tuple[2]);
const setRotTuple = (obj3d, tuple) => obj3d.rotation.set(tuple[0], tuple[1], tuple[2]);

// ────────────────────────────────────────────────────────────────────────────
class Model3DBanner extends HTMLElement {
  constructor() {
    super();
    this._mounted = false;
    this._initStarted = false;
    this._dirty = true;
    this._rafQueued = false;
    this._resizeQueued = false;
    this._mappingRefreshQueued = false;
    this._currentFrame = 0;
    this._headerOffset = 0;
    this._headerHeight = 0;
    this._sectionTop = 0;
    this._sectionHeight = 0;
    this._viewportH = window.innerHeight;
    this._dollyBack = 0;
    this._lastW = 0;
    this._lastH = 0;
    this._lastDpr = 0;
    this._demoteStage = 0;
    this._demoteDprCap = 0;
    this._smaaPass = null;
    this._lastScrollAt = 0;
    this._lastProgressText = '';
    this._lastSignupOpacity = -1;
    this._scrolling = false;
    this._gpuSamples = [];
    this._disposed = false;
    this._perfEMA = 0;
    this._perfSamples = 0;
    this._slotsLoaded = 0;
    this._shadowBakedKey = '';
    // 'waiting' → nothing is painted and the still shows; 'running' → the
    // reveal blend is playing; 'done' → scroll drives the scene directly.
    this._revealState = 'waiting';
    this._revealStart = 0;
    this._fallbackHidden = false;
    this._cancelScheduledStart = null;
  }

  connectedCallback() {
    if (this._mounted) return;
    this._mounted = true;

    let urls;
    try {
      urls = JSON.parse(this.dataset.assets);
    } catch (e) {
      console.error('[model-3d-banner] failed to parse data-assets', e);
      return;
    }
    this._urls = urls;

    this._canvas = this.querySelector('.model-3d-banner__canvas');
    // Renderer + camera size off the inner frame (max 1920×960), not the full
    // sticky stage. The stage centers the frame on desktop and stacks
    // frame-above-intro on mobile.
    this._frame = this.querySelector('.model-3d-banner__frame');
    // Eight module labels (indexed by insertion order) plus, when the section
    // provides it, a separate closing label at the bottom of the frame.
    this._labels = Array.from(
      this.querySelectorAll('.model-3d-banner__label:not(.model-3d-banner__label--ending)'),
    );
    this._endingLabel = this.querySelector('.model-3d-banner__label--ending');
    this._progressEl = this.querySelector('[data-progress]');
    this._fallback = this.querySelector('.model-3d-banner__fallback');
    this._intro = this.querySelector('.model-3d-banner__intro');
    // The heading stays up throughout; only the signup (subheading + form)
    // is timeline-driven, arriving once the camera settles at the end.
    this._signup = ['.model-3d-banner__subheading', '.model-3d-banner__newsletter']
      .map((sel) => this.querySelector(sel))
      .filter(Boolean);
    // Desktop shows the signup only at the end of the animation; mobile
    // stacks the intro below the canvas and always shows all of it.
    this._overlayMQ = window.matchMedia('(min-width: 750px)');

    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    const mobileMQ = window.matchMedia('(max-width: 749px)');
    const desktopOnly = this.classList.contains('model-3d-banner--desktop-only');
    const webglMissing = typeof window.WebGL2RenderingContext === 'undefined';
    const saveData = navigator.connection && navigator.connection.saveData;

    if (reducedMotion.matches || webglMissing || saveData || (desktopOnly && mobileMQ.matches)) {
      this.classList.add('model-3d-banner--using-fallback');
      // The mobile override is the only viewport-dependent gate — re-check when
      // the viewport grows past the breakpoint (e.g. tablet rotation).
      if (desktopOnly && !reducedMotion.matches && !webglMissing && !saveData) {
        mobileMQ.addEventListener('change', (e) => {
          if (!e.matches && !this._initStarted) {
            this.classList.remove('model-3d-banner--using-fallback');
            this._scheduleStart();
          }
        });
      }
      return;
    }

    this._scheduleStart();
  }

  // See START_* above. Idempotent; disconnectedCallback cancels it.
  _scheduleStart() {
    if (this._initStarted || this._cancelScheduledStart) return;
    let idleHandle = 0;
    let timer = 0;
    let waitingForIdle = false;
    const still = this._fallback;
    const onIntent = () => start();
    const afterStill = () => {
      if (waitingForIdle) return; // load and error are both wired up
      waitingForIdle = true;
      if (typeof window.requestIdleCallback === 'function') {
        idleHandle = window.requestIdleCallback(start, { timeout: START_IDLE_TIMEOUT_MS });
      } else {
        timer = setTimeout(start, START_NO_IDLE_DELAY_MS);
      }
    };
    const cancel = () => {
      this._cancelScheduledStart = null;
      if (still) {
        still.removeEventListener('load', afterStill);
        still.removeEventListener('error', afterStill);
      }
      for (const [type, capture] of START_INTENT_EVENTS) window.removeEventListener(type, onIntent, capture);
      if (idleHandle) window.cancelIdleCallback(idleHandle);
      clearTimeout(timer);
    };
    const start = () => {
      cancel();
      if (!this._disposed) this._start();
    };
    this._cancelScheduledStart = cancel;
    for (const [type, capture] of START_INTENT_EVENTS) {
      window.addEventListener(type, onIntent, { capture, passive: true });
    }
    // A broken still must not hold the scene back either, hence 'error'.
    if (still && !still.complete) {
      still.addEventListener('load', afterStill);
      still.addEventListener('error', afterStill);
    } else {
      afterStill();
    }
  }

  _start() {
    if (this._initStarted) return;
    this._initStarted = true;
    this._init().catch((err) => {
      console.error('[model-3d-banner] init failed', err);
      this.classList.add('model-3d-banner--using-fallback');
    });
  }

  async _init() {
    this._tier = pickTier(this);
    await loadThree(this.dataset.threeBundle);
    const tier = QUALITY_TIERS[this._tier];
    const useComposer = tier.composer && (window.devicePixelRatio || 1) < 1.5;

    this._renderer = new THREE.WebGLRenderer({
      canvas: this._canvas,
      antialias: !useComposer,
      alpha: true,
      powerPreference: 'high-performance',
    });
    this._renderer.outputColorSpace = THREE.SRGBColorSpace;
    // With the composer, OutputPass handles ACES tone mapping; without it the
    // renderer does, so both pipelines land on the same look (minus bloom).
    this._renderer.toneMapping = THREE.ACESFilmicToneMapping;
    this._gl = this._renderer.getContext();
    this._gpuTimer = this._gl.getExtension('EXT_disjoint_timer_query_webgl2');

    this._scene = new THREE.Scene();

    // env via PMREM from a scene of emissive planes (lightformer analogues).
    const pmrem = new THREE.PMREMGenerator(this._renderer);
    pmrem.compileEquirectangularShader();
    const envScene = buildEnvScene();
    this._envRT = pmrem.fromScene(envScene, 0, 0.01, 100);
    this._scene.environment = this._envRT.texture;
    pmrem.dispose();
    envScene.traverse((o) => {
      if (o.geometry) o.geometry.dispose();
      if (o.material) o.material.dispose();
    });

    // baseline lights matched to the R3F version.
    this._scene.add(new THREE.HemisphereLight(0x1c1f28, 0x000000, 0.08));
    const d1 = new THREE.DirectionalLight(0xfff3dc, 1.6);
    d1.position.set(-0.5, 0.8, 1.6);
    const d2 = new THREE.DirectionalLight(0xcfe0ff, 1.4);
    d2.position.set(1.1, 0.6, 1.0);
    const d3 = new THREE.DirectionalLight(0xffffff, 0.25);
    d3.position.set(0.4, -0.8, 0.2);
    this._scene.add(d1, d2, d3);

    // No opaque floor mesh: the canvas is transparent and the CSS color-scheme
    // background shows through, so the section blends with the page instead of
    // rendering its own near-black plane (which produced a visible seam). The
    // baked contact-shadow plane below still grounds the rack.

    // perspective camera — used throughout. No more perspective→ortho swap.
    this._perspCam = new THREE.PerspectiveCamera(FOV, BASE_ASPECT, 0.01, 1000);
    setVec3Tuple(this._perspCam, MIDDLE_CAMERA.position);
    setRotTuple(this._perspCam, MIDDLE_CAMERA.rotation);

    // load chassis + knob + screen texture before anything else so the first
    // rendered frame already shows the rack; the modules stream in behind it.
    const [chassis, knob, screenTex] = await Promise.all([
      loadGLB(this._urls.chassis),
      loadGLB(this._urls.knob),
      new Promise((res, rej) =>
        new THREE.TextureLoader().load(this._urls.screen, res, undefined, rej),
      ),
    ]);
    this._chassis = chassis.clone(true);
    this._scene.add(this._chassis);
    this._knobSource = knob;

    // PD8X screen insert plane (textured).
    screenTex.colorSpace = THREE.SRGBColorSpace;
    screenTex.anisotropy = this._renderer.capabilities.getMaxAnisotropy();
    const screenGroup = new THREE.Group();
    screenGroup.position.set(0.085, 0, 0.045);
    const screenMesh = new THREE.Mesh(
      new THREE.PlaneGeometry(1, 1),
      new THREE.MeshBasicMaterial({
        map: screenTex,
        toneMapped: true,
        color: new THREE.Color(1.35, 1.35, 1.35),
        side: THREE.DoubleSide,
        depthTest: false,
        depthWrite: false,
      }),
    );
    screenMesh.rotation.set(Math.PI / 2, 0, 0);
    screenMesh.scale.set(0.08, 0.08, 0.08);
    screenGroup.add(screenMesh);
    // Treat the LCD as a luminous surface in both pipelines. Keeping it in
    // the main scene preserves occlusion by modules during insertion; a
    // fullscreen tone-mapping pass cannot exempt an individual material.
    this._scene.add(screenGroup);

    // knob instances — one per slot, identical source mesh, per-frame rotation.
    this._knobs = KNOBS.map((cfg) => {
      const g = new THREE.Group();
      setVec3Tuple(g, cfg.position);
      setRotTuple(g, cfg.startRotation);
      g.add(this._knobSource.clone(true));
      this._scene.add(g);
      return { group: g, cfg };
    });

    // slot containers — geometry streams in async; we keep the array indexed by
    // insertionIndex (0..7) since SLOT_INSERTION_ORDER drives timing.
    const maxAnisotropy = this._renderer.capabilities.getMaxAnisotropy();
    this._slots = SLOT_INSERTION_ORDER.map((slotIndex, insertionIndex) => {
      const group = new THREE.Group();
      const cfg = SLOTS[slotIndex];
      setVec3Tuple(group, cfg.external);
      this._scene.add(group);
      return {
        slotIndex,
        insertionIndex,
        cfg,
        group,
        materials: [],
        slideStartFrame: getSlotSlideStartFrame(insertionIndex),
        loaded: false,
      };
    });

    // Contact shadow plane. Its texture is (re)baked in _renderFrame whenever
    // an occluder moves or another module finishes streaming in.
    this._contactShadows = new ContactShadows(this._renderer, CONTACT_SHADOW_OPTIONS);
    this._scene.add(this._contactShadows.group);

    // stream slot models. Nothing is painted until they are all in (or the
    // reveal timeout fires): the reveal below opens on the fully seated
    // chassis the still shows, which needs every module present.
    const slotLoads = SLOT_INSERTION_ORDER.map((slotIndex, insertionIndex) => {
      const url = this._urls.slots[slotIndex];
      return loadGLB(url)
        .then((sourceScene) => {
          const { root, materials } = prepareClonedSlot(sourceScene, maxAnisotropy);
          const slot = this._slots[insertionIndex];
          slot.group.add(root);
          slot.materials = materials;
          slot.loaded = true;
          for (const m of materials) m.opacity = 0;
          // Part of the shadow bake key: a module that arrives after the
          // reveal timeout gets its shadow even if the scroll position hasn't
          // changed.
          this._slotsLoaded += 1;
          this._markDirty();
        })
        .catch((err) => {
          // One failed module download leaves its slot empty but must not
          // break the rest of the animation.
          console.error('[model-3d-banner] slot model failed to load', url, err);
        });
    });

    // postprocessing pipeline (high/medium only) — multisampled RT for
    // geometry AA, bloom, output (ACES), then SMAA on the high tier to clean
    // up the specular shimmer MSAA can't address (metallic connectors/screws).
    if (useComposer) {
      const samples = Math.min(tier.samples, this._renderer.capabilities.maxSamples || 4);
      this._composer = new EffectComposerCtor(
        this._renderer,
        new THREE.WebGLRenderTarget(1, 1, {
          type: THREE.HalfFloatType,
          samples,
        }),
      );
      this._renderPass = new RenderPassCtor(this._scene, this._perspCam);
      this._composer.addPass(this._renderPass);
      this._bloomPass = new UnrealBloomPassCtor(new THREE.Vector2(1, 1), 0.12, 0.25, 2.0);
      // UnrealBloom's composite shader multiplies each blur level by
      // vec4(tint, 1.0), so it writes alpha = bloomStrength * sum(factors)
      // (about 0.84) into every pixel. Blended additively onto the frame,
      // that painted a ~75% black wash over the page behind the "transparent"
      // canvas — the seam below the header on dark schemes, and a dark block
      // on light ones. Carry only as much alpha as the glow is bright so empty
      // pixels stay clear and the scheme background genuinely shows through.
      const composite = this._bloomPass.compositeMaterial;
      composite.fragmentShader = composite.fragmentShader
        .replace('gl_FragColor = bloomStrength * (', 'vec4 nfBloom = bloomStrength * (')
        .replace(
          /(vec4 nfBloom = bloomStrength \* \([\s\S]*?\);)/,
          '$1\n\t\t\t\t\tgl_FragColor = vec4(nfBloom.rgb, clamp(max(max(nfBloom.r, nfBloom.g), nfBloom.b), 0.0, 1.0));',
        );
      composite.needsUpdate = true;
      this._composer.addPass(this._bloomPass);
      this._composer.addPass(new OutputPassCtor());
      if (tier.smaa) {
        this._smaaPass = new SMAAPassCtor(1, 1);
        this._composer.addPass(this._smaaPass);
      }
    }
    this._applyDemoteStage(readStoredDemoteStage());

    this._setupHeaderSync();
    this._refreshScrollMapping();
    this._resize();
    this._readScroll();
    this._markDirty();

    // Scroll handler does no DOM reads: the section-top/height/viewport
    // numbers are cached and refreshed by the observers below, so scrolling
    // never forces layout.
    this._onScroll = () => {
      this._lastScrollAt = performance.now();
      if (!this._scrolling) {
        this._scrolling = true;
        if (this._demoteDprCap) this._resize();
      }
      clearTimeout(this._settleTimer);
      this._settleTimer = setTimeout(() => {
        this._scrolling = false;
        // A slow GPU may use fewer pixels in motion, but paused products must
        // always return to native/budgeted resolution. No idle animation loop.
        if (this._demoteDprCap) this._resize();
      }, 220);
      this._readScroll();
    };
    window.addEventListener('scroll', this._onScroll, { passive: true });
    this._watchDpr();
    this._onVisibilityChange = () => {
      cancelAnimationFrame(this._perfProbeRaf);
      this._perfProbeRaf = 0;
      this._perfEMA = 0;
      this._perfSamples = 0;
      if (document.hidden) {
        clearTimeout(this._settleTimer);
        this._scrolling = false;
      } else {
        this._resize();
        this._markDirty();
      }
    };
    document.addEventListener('visibilitychange', this._onVisibilityChange);

    // Frame resizes (viewport resize, orientation, mobile stacking changes)
    // arrive via ResizeObserver, rAF-coalesced in _resize's dedupe.
    this._frameRO = new ResizeObserver(() => {
      if (this._resizeQueued) return;
      this._resizeQueued = true;
      requestAnimationFrame(() => {
        this._resizeQueued = false;
        this._resize();
      });
    });
    this._frameRO.observe(this._frame);

    // Content above the section loading in (images, videos) shifts our
    // document-space top; body size changes are the cheap proxy for that.
    this._bodyRO = new ResizeObserver(() => this._queueMappingRefresh());
    this._bodyRO.observe(document.body);
    this._onOrientation = () => this._queueMappingRefresh();
    window.addEventListener('orientationchange', this._onOrientation);

    // Hold the still until the modules are seated, then hand off through the
    // reveal (see REVEAL_* above). The still is hidden by _renderFrame once
    // the first live frame is actually on the canvas, never before.
    await Promise.race([
      Promise.all(slotLoads), // each load already catches, so this never rejects
      new Promise((resolve) => setTimeout(resolve, REVEAL_LOAD_TIMEOUT_SECONDS * 1000)),
    ]);
    this._revealState = 'running';
    this._markDirty();
  }

  // ── header offset: observed, not polled ────────────────────────────────────
  // Dawn's StickyHeader toggles shopify-section-header-sticky/-hidden classes
  // on the header section wrapper. We mirror that into a CSS var that shifts
  // the sticky stage's `top` — it no longer affects the frame's size, so a
  // header reveal never resizes the canvas.
  _setupHeaderSync() {
    this._headerEl = document.querySelector('.section-header');
    if (!this._headerEl) return;
    this._headerHeight = this._headerEl.offsetHeight || 0;

    const update = () => {
      const sticky =
        this._headerEl.classList.contains('shopify-section-header-sticky') &&
        !this._headerEl.classList.contains('shopify-section-header-hidden');
      const next = sticky ? this._headerHeight : 0;
      if (next !== this._headerOffset) {
        this._headerOffset = next;
        this.style.setProperty('--model-3d-banner-header-offset', `${next}px`);
      }
    };

    this._headerMO = new MutationObserver(update);
    this._headerMO.observe(this._headerEl, { attributes: true, attributeFilter: ['class'] });
    this._headerRO = new ResizeObserver((entries) => {
      const h = Math.round(entries[0].contentRect.height);
      if (h && h !== this._headerHeight) {
        this._headerHeight = h;
        update();
      }
    });
    this._headerRO.observe(this._headerEl);
    update();
  }

  _queueMappingRefresh() {
    if (this._mappingRefreshQueued) return;
    this._mappingRefreshQueued = true;
    requestAnimationFrame(() => {
      this._mappingRefreshQueued = false;
      this._refreshScrollMapping();
      this._readScroll();
    });
  }

  _refreshScrollMapping() {
    const rect = this.getBoundingClientRect();
    this._sectionTop = rect.top + window.scrollY;
    this._sectionHeight = rect.height;
    this._viewportH = window.innerHeight;
  }

  _effectiveDpr(w, h) {
    const tier = QUALITY_TIERS[this._tier];
    const native = window.devicePixelRatio || 1;
    const budgetCap = Math.sqrt(tier.pixelBudget / Math.max(1, w * h));
    return Math.min((this._scrolling && this._demoteDprCap) || Infinity, tier.dpr, native, budgetCap);
  }

  _watchDpr() {
    this._dprMQ?.removeEventListener('change', this._onDprChange);
    this._dprMQ = window.matchMedia(`(resolution: ${window.devicePixelRatio || 1}dppx)`);
    this._onDprChange = () => {
      this._watchDpr();
      this._resize();
    };
    this._dprMQ.addEventListener('change', this._onDprChange);
  }

  _resize() {
    const w = this._frame.clientWidth;
    const h = this._frame.clientHeight;
    if (!w || !h) return;
    const dpr = this._effectiveDpr(w, h);
    if (w === this._lastW && h === this._lastH && dpr === this._lastDpr) return;
    this._lastW = w;
    this._lastH = h;
    this._lastDpr = dpr;
    this._renderer.setPixelRatio(dpr);
    this._renderer.setSize(w, h, false);
    // Composer keeps its own pixel ratio; renderer.setPixelRatio alone leaves
    // every effect at CSS resolution and then stretches that image on Retina.
    this._composer?.setPixelRatio(dpr);
    this._composer?.setSize(w, h);
    this._updateProjection(w, h);
    this._markDirty();
  }

  _updateProjection(w, h) {
    const aspect = w / h;
    // Reference frame: the 2:1 composition at this width. A frame shorter
    // than that (aspect > 2) shows the top part of the reference; a taller
    // one (phones) is the reference itself, with the FOV widened below.
    const refH = aspect > BASE_ASPECT ? w / BASE_ASPECT : h;
    const refAspect = w / refH;
    let fov = FOV;
    this._dollyBack = 0;
    if (refAspect < BASE_ASPECT) {
      // Preserve the horizontal coverage the scene was composed for: widen the
      // vertical FOV up to MAX_FOV, then dolly back for whatever remains.
      const halfW = Math.tan(deg(FOV) / 2) * BASE_ASPECT;
      const needed = 2 * Math.atan(halfW / refAspect);
      const maxRad = deg(MAX_FOV);
      if (needed > maxRad) {
        const coverage = Math.tan(maxRad / 2) * refAspect;
        this._dollyBack = CAMERA_TARGET_DIST * (halfW / coverage - 1);
        fov = MAX_FOV;
      } else {
        fov = (needed * 180) / Math.PI;
      }
    }
    // Lens shift (see VIEW_SHIFT_UP): the camera describes a virtual image
    // (1 + shift) reference-heights tall whose vertical FOV is widened to
    // match, and the canvas shows the h-pixel window that starts shift ×
    // refH below its top. Horizontal coverage and pixel scale are unchanged;
    // the window's centre sits shift × refH / 2 above the optical axis.
    const shift = VIEW_SHIFT_UP;
    const cam = this._perspCam;
    cam.fov = (2 * Math.atan(Math.tan(deg(fov) / 2) * (1 + shift)) * 180) / Math.PI;
    cam.aspect = refAspect / (1 + shift);
    cam.setViewOffset(w, refH * (1 + shift), 0, refH * shift, w, h);
    cam.updateProjectionMatrix();
  }

  _readScroll() {
    const previous = this._currentFrame;
    const scrollable = this._sectionHeight - this._viewportH;
    if (scrollable <= 0) {
      this._currentFrame = 0;
    } else {
      const traveled = Math.min(scrollable, Math.max(0, window.scrollY - this._sectionTop));
      this._currentFrame = (traveled / scrollable) * TOTAL_FRAMES;
    }
    if (this._currentFrame !== previous) this._markDirty();
  }

  _markDirty() {
    if (this._disposed) return;
    this._dirty = true;
    if (this._rafQueued) return;
    this._rafQueued = true;
    requestAnimationFrame(() => {
      this._rafQueued = false;
      if (!this._dirty) return;
      this._dirty = false;
      this._renderFrame(this._currentFrame);
    });
  }

  // ── runtime demotion ────────────────────────────────────────────────────────
  // Measure render work, never gaps between scroll events. An event-driven
  // animation can receive one cheap update every 100ms without being slow.
  // GPU queries are asynchronous and bounded; CPU submission time is the
  // fallback when browsers do not expose EXT_disjoint_timer_query_webgl2.
  _notePerfSample(costMs) {
    if (!Number.isFinite(costMs) || costMs <= 0) return;
    this._perfEMA = this._perfSamples === 0 ? costMs : this._perfEMA * 0.9 + costMs * 0.1;
    this._perfSamples += 1;
    if ((this._perfSamples >= 30 && this._perfEMA > 24) ||
        (this._perfSamples >= 5 && this._perfEMA > 60)) this._demote();
  }

  _pollGpuSamples() {
    if (!this._gpuTimer) return;
    const gl = this._gl;
    const disjoint = gl.getParameter(this._gpuTimer.GPU_DISJOINT_EXT);
    this._gpuSamples = this._gpuSamples.filter((sample) => {
      if (!disjoint && !gl.getQueryParameter(sample.query, gl.QUERY_RESULT_AVAILABLE)) return true;
      if (!disjoint) {
        const gpuMs = gl.getQueryParameter(sample.query, gl.QUERY_RESULT) / 1e6;
        this._notePerfSample(Math.max(sample.cpuMs, gpuMs));
      }
      gl.deleteQuery(sample.query);
      return false;
    });
  }

  _beginPerfSample() {
    this._pollGpuSamples();
    if (this._revealState !== 'done' || !this._scrolling) return null;
    let query = null;
    if (this._gpuTimer && this._gpuSamples.length < 4 && !this._gl.getParameter(this._gpuTimer.GPU_DISJOINT_EXT)) {
      query = this._gl.createQuery();
      if (query) this._gl.beginQuery(this._gpuTimer.TIME_ELAPSED_EXT, query);
    }
    return { query, start: performance.now() };
  }

  _endPerfSample(sample) {
    if (!sample) return;
    const cpuMs = performance.now() - sample.start;
    if (sample.query) {
      this._gl.endQuery(this._gpuTimer.TIME_ELAPSED_EXT);
      this._gpuSamples.push({ query: sample.query, cpuMs });
    } else if (!this._perfProbeRaf) {
      // Submission time can be tiny while a software GPU takes hundreds of
      // milliseconds to finish. One scheduled next-frame probe includes that
      // work, without ever sampling the idle gap between input events. Allow
      // one normal 60Hz frame of scheduling latency (30Hz displays also stay
      // below the 24ms threshold). Hidden-tab throttling is not a GPU signal.
      this._perfProbeRaf = requestAnimationFrame((nextFrame) => {
        this._perfProbeRaf = 0;
        if (this._disposed || document.hidden) return;
        this._notePerfSample(Math.max(cpuMs, nextFrame - sample.start - 1000 / 60));
      });
    }
  }

  // Apply cumulative demotion stages: effects first (SMAA, then bloom), and
  // only then reduce resolution — sharpness is the last thing to give up.
  _applyDemoteStage(stage) {
    this._demoteStage = stage;
    this._demoteDprCap = 0;
    for (let i = 0; i < stage; i++) {
      const action = DEMOTE_STAGES[i];
      if (action === 'smaa-off' && this._smaaPass) this._smaaPass.enabled = false;
      if (action === 'bloom-off' && this._bloomPass) this._bloomPass.enabled = false;
      if (action === 'dpr-1.5') this._demoteDprCap = 1.5;
      if (action === 'dpr-1') this._demoteDprCap = 1;
    }
    this.dataset.tierActive = this._tier + (stage ? `-d${stage}` : '');
    this._lastDpr = 0; // force _resize to re-evaluate DPR
    this._resize();
  }

  _demote() {
    this._perfSamples = 0;
    this._perfEMA = 0;
    if (this._demoteStage >= DEMOTE_STAGES.length) return;
    let next = this._demoteStage + 1;
    // Native MSAA has no post effects to disable. Do not spend sixty slow
    // frames walking through empty effect stages before adapting resolution.
    while (next <= DEMOTE_STAGES.length) {
      const action = DEMOTE_STAGES[next - 1];
      if ((action === 'smaa-off' && !this._smaaPass?.enabled) ||
          (action === 'bloom-off' && !this._bloomPass?.enabled)) next += 1;
      else break;
    }
    next = Math.min(next, DEMOTE_STAGES.length);
    storeDemoteStage(next);
    this._applyDemoteStage(next);
  }

  // ── pose: the scene state for a timeline frame, as plain numbers ──────────
  // Kept separate from applying it so the reveal can blend the resting pose
  // with the live scroll pose without touching the objects twice.
  _poseAt(frame) {
    // slots: opacity fade then slide-in from external → internal
    const slots = this._slots.map((slot) => {
      const slideStart = slot.slideStartFrame;
      const fadeEnd = slideStart - holdFrames;
      return {
        opacity: interpolate(frame, [fadeEnd - fadeFrames, fadeEnd], [0, 1], FADE_EASING),
        position: interpolateVec3(
          frame,
          slideStart,
          slideStart + slideFrames,
          slot.cfg.external,
          slot.cfg.internal,
        ),
      };
    });

    // knobs (indexed by insertionIndex here): rotate after their slot seats
    const knobs = SLOT_INSERTION_ORDER.map((slotIndex, insertionIndex) => {
      const cfg = KNOBS[slotIndex];
      const knobStart = getSlotSlideStartFrame(insertionIndex) + slideFrames;
      return interpolateVec3(
        frame,
        knobStart,
        knobStart + knobRotationFrames,
        cfg.startRotation,
        cfg.endRotation,
        KNOB_EASING,
      );
    });

    // camera: hold @ MIDDLE until last slot+knob done, then lerp to ENDING.
    // Stays in perspective the entire way — interpolate clamps so frames past
    // cameraTransitionEndFrame hold the final pose.
    let cameraPosition;
    let cameraRotation;
    if (frame < cameraTransitionStartFrame) {
      cameraPosition = MIDDLE_CAMERA.position;
      cameraRotation = MIDDLE_CAMERA.rotation;
    } else {
      cameraPosition = interpolateVec3(
        frame,
        cameraTransitionStartFrame,
        cameraTransitionEndFrame,
        MIDDLE_CAMERA.position,
        ENDING_CAMERA.position,
      );
      cameraRotation = interpolateVec3(
        frame,
        cameraTransitionStartFrame,
        cameraTransitionEndFrame,
        MIDDLE_CAMERA.rotation,
        ENDING_CAMERA.rotation,
      );
    }

    return { slots, knobs, cameraPosition, cameraRotation, signupOpacity: this._signupOpacityAt(frame) };
  }

  // Reveal blend, t ∈ [0,1] from the resting pose toward the live one. The
  // channels run on different clocks so it reads as "modules go, then the
  // chassis turns": opacity is finished REVEAL_FADE_PORTION of the way in,
  // position eases in (a small eject while still visible, the rest of the
  // travel happens unseen), knobs and camera ease in-out across the whole
  // second. Every channel reaches exactly `to` at t = 1, so the scroll
  // timeline takes over without a snap.
  _blendPose(from, to, t) {
    const opacityW = easeInOutCubic(clamp01(t / REVEAL_FADE_PORTION));
    const positionW = t * t;
    const moveW = easeInOutCubic(t);
    return {
      slots: from.slots.map((a, i) => ({
        opacity: lerp(a.opacity, to.slots[i].opacity, opacityW),
        position: lerpVec3(a.position, to.slots[i].position, positionW),
      })),
      knobs: from.knobs.map((a, i) => lerpVec3(a, to.knobs[i], moveW)),
      cameraPosition: lerpVec3(from.cameraPosition, to.cameraPosition, moveW),
      cameraRotation: lerpVec3(from.cameraRotation, to.cameraRotation, moveW),
      signupOpacity: lerp(from.signupOpacity, to.signupOpacity, t),
    };
  }

  _applyPose(pose) {
    this._slots.forEach((slot, i) => {
      const opacity = pose.slots[i].opacity;
      slot.group.visible = opacity > 0;
      for (const m of slot.materials) {
        m.opacity = opacity;
        // Keep two-sided transparency during the fade; a fully opaque module
        // needs only one draw per surface, even with a DoubleSide material.
        const singlePass = opacity === 1;
        if (m.forceSinglePass !== singlePass) {
          m.forceSinglePass = singlePass;
          // Three's transparent double pass temporarily compiles FrontSide
          // shaders. Restore a DoubleSide program when the opaque pass starts.
          m.needsUpdate = true;
        }
      }
      setVec3Tuple(slot.group, pose.slots[i].position);
    });
    SLOT_INSERTION_ORDER.forEach((slotIndex, insertionIndex) => {
      setRotTuple(this._knobs[slotIndex].group, pose.knobs[insertionIndex]);
    });
    setVec3Tuple(this._perspCam, pose.cameraPosition);
    setRotTuple(this._perspCam, pose.cameraRotation);
  }

  _renderFrame(frame) {
    // While waiting for the slot models nothing is painted: the transparent
    // canvas lets the still show through, and the scroll/resize observers
    // keep the cached mapping fresh for when the reveal starts.
    if (this._disposed || !this._renderer || this._revealState === 'waiting') return;
    const sample = this._beginPerfSample();

    const now = performance.now();
    let revealT = 1;
    if (this._revealState === 'running') {
      // _revealStart is stamped after the first live frame is painted (that
      // frame also compiles the shaders, and rAF never fires in a background
      // tab), so the hold and the blend always play out where they can be
      // seen. Until then t = 0: the resting pose the still shows.
      revealT = this._revealStart
        ? clamp01(((now - this._revealStart) / 1000 - REVEAL_HOLD_SECONDS) / REVEAL_SECONDS)
        : 0;
      if (revealT >= 1) this._revealState = 'done';
    }
    const revealing = this._revealState === 'running';

    const target = this._poseAt(frame);
    let pose = target;
    if (revealing) {
      const from = this._poseAt(TOTAL_FRAMES);
      // The signup is hidden while the still shows (CSS), so the reveal must
      // not flash it in; it only fades up if the live frame wants it.
      from.signupOpacity = 0;
      pose = this._blendPose(from, target, revealT);
    }
    this._applyPose(pose);

    // ── contact shadow: re-bake whenever an occluder has moved ──────────────
    // Past cameraTransitionStartFrame only the camera moves, and the shadow
    // camera is fixed, so the key stops changing there and while idle. During
    // the reveal every frame moves the modules.
    if (this._contactShadows) {
      const shadowKey = revealing
        ? 'reveal:' + revealT
        : Math.min(frame, cameraTransitionStartFrame) + ':' + this._slotsLoaded;
      if (shadowKey !== this._shadowBakedKey) {
        this._shadowBakedKey = shadowKey;
        this._contactShadows.bake(this._scene);
      }
    }

    if (this._dollyBack) this._perspCam.translateZ(this._dollyBack);
    this._perspCam.updateMatrixWorld();

    // No module labels during the reveal; the timeline's own label for the
    // live frame appears the moment it ends.
    if (!revealing) this._updateLabels(frame);
    this._applySignupOpacity(pose.signupOpacity);

    if (this._progressEl) {
      const text = Math.round((Math.min(frame, TOTAL_FRAMES) / TOTAL_FRAMES) * 100) + '%';
      if (text !== this._lastProgressText) {
        this._lastProgressText = text;
        this._progressEl.textContent = text;
      }
    }

    if (this._composer) this._composer.render();
    else this._renderer.render(this._scene, this._perspCam);


    // The still goes only once its replacement is on the canvas, and in the
    // same frame: the canvas sits above the <img>, and both pictures carry the
    // contact shadow with partial alpha, so any overlap (the old 0.4 s CSS
    // fade) stacks the two shadows and darkens them for its duration.
    if (!this._fallbackHidden) {
      this._fallbackHidden = true;
      if (this._fallback) this._fallback.classList.add('model-3d-banner__fallback--hidden');
    }
    if (revealing) {
      if (!this._revealStart) this._revealStart = performance.now();
      this._markDirty(); // keep ticking until the blend completes
    }

    this._endPerfSample(sample);
  }

  _updateLabels(frame) {
    // Active label = the most recent insertionIndex whose label has started.
    // Transitions are SEQUENCED, not crossfaded: label i fades fully out over
    // [nextStart, nextStart+fadeFrames] before label i+1 fades in over
    // [start+fadeFrames, start+2*fadeFrames] — the boxes occupy the same spot,
    // so overlapping fades produced garbled double-exposed text.
    // Labels that stay at opacity 0 get no style writes at all, and visibility
    // is class-gated so hidden labels cost the compositor nothing.
    //
    // The closing PD8X label is its own element at the bottom of the frame
    // (the head-on rack fills the top at the end): the last module label
    // fades out as the camera starts moving and the closing one fades in
    // after it, same sequencing as between modules. If the section has no
    // such element the last module label swaps its text in place instead.
    const endingEl = this._endingLabel;
    const write = (el, opacity, fadeInStart, title, detail) => {
      if (opacity === 0 && el._lastOpacity === 0) return;
      el._lastOpacity = opacity;
      el.classList.toggle('model-3d-banner__label--active', opacity > 0);
      const translateY = interpolate(frame, [fadeInStart, fadeInStart + fadeFrames], [8, 0], FADE_EASING);
      el.style.opacity = opacity;
      el.style.transform = `translateY(${translateY}px)`;
      if (el._lastTitle !== title) {
        el.querySelector('.model-3d-banner__label-title').textContent = title;
        el._lastTitle = title;
      }
      if (el._lastDetail !== detail) {
        el.querySelector('.model-3d-banner__label-detail').textContent = detail;
        el._lastDetail = detail;
      }
    };

    SLOT_INSERTION_ORDER.forEach((slotIndex, insertionIndex) => {
      const el = this._labels[insertionIndex];
      if (!el) return;
      const labelStart = getLabelStartFrame(insertionIndex);
      const isLast = insertionIndex === SLOT_INSERTION_ORDER.length - 1;
      const fadeInStart = insertionIndex === 0 ? labelStart : labelStart + fadeFrames;
      const fadeIn = interpolate(frame, [fadeInStart, fadeInStart + fadeFrames], [0, 1], FADE_EASING);
      let nextStart = isLast ? null : getLabelStartFrame(insertionIndex + 1);
      if (isLast && endingEl) nextStart = cameraTransitionStartFrame;
      const fadeOut =
        nextStart === null
          ? 1
          : interpolate(frame, [nextStart, nextStart + fadeFrames], [1, 0], FADE_EASING);
      const opacity = Math.min(fadeIn, fadeOut);

      const showEnding = isLast && !endingEl && frame >= cameraTransitionStartFrame;
      const title = showEnding ? ENDING_LABEL_TITLE : MODULE_LABELS[slotIndex];
      const detail = showEnding ? ENDING_LABEL_DETAIL : MODULE_DETAILS[slotIndex];
      write(el, opacity, fadeInStart, title, detail);
    });

    if (endingEl) {
      const fadeInStart = cameraTransitionStartFrame + fadeFrames;
      const opacity = interpolate(frame, [fadeInStart, fadeInStart + fadeFrames], [0, 1], FADE_EASING);
      write(endingEl, opacity, fadeInStart, ENDING_LABEL_TITLE, ENDING_LABEL_DETAIL);
    }
  }

  // Desktop: the intro sits in the band the CSS reserves below the frame, so
  // the heading can stay up for the whole animation without a module ever
  // crossing it. The signup (subheading + newsletter form) is the payoff: it
  // is hidden by the stylesheet while the still shows and through the
  // insertion sequence, and fades up as the camera settles into the ending
  // pose. Mobile stacks the intro below the canvas and shows all of it.
  _signupOpacityAt(frame) {
    return interpolate(frame, [cameraTransitionStartFrame, cameraTransitionEndFrame], [0, 1]);
  }

  _applySignupOpacity(opacity) {
    if (!this._signup.length) return;
    if (!this._overlayMQ.matches) {
      if (this._lastSignupOpacity !== 1) {
        this._lastSignupOpacity = 1;
        for (const el of this._signup) {
          el.style.opacity = '';
          el.style.visibility = '';
        }
      }
      return;
    }
    if (opacity === this._lastSignupOpacity) return;
    this._lastSignupOpacity = opacity;
    for (const el of this._signup) {
      el.style.opacity = opacity;
      // The stylesheet default is hidden, so 'visible' must be explicit; it
      // also gates pointer/tab interaction with the invisible form.
      el.style.visibility = opacity < 0.05 ? 'hidden' : 'visible';
    }
  }

  disconnectedCallback() {
    this._disposed = true;
    this._cancelScheduledStart?.();
    clearTimeout(this._settleTimer);
    cancelAnimationFrame(this._perfProbeRaf);
    this._dprMQ?.removeEventListener('change', this._onDprChange);
    for (const sample of this._gpuSamples) this._gl.deleteQuery(sample.query);
    this._gpuSamples = [];
    if (this._onVisibilityChange) document.removeEventListener('visibilitychange', this._onVisibilityChange);
    if (this._onScroll) window.removeEventListener('scroll', this._onScroll);
    if (this._onOrientation) window.removeEventListener('orientationchange', this._onOrientation);
    this._frameRO?.disconnect();
    this._bodyRO?.disconnect();
    this._headerRO?.disconnect();
    this._headerMO?.disconnect();
    this._envRT?.dispose();
    this._contactShadows?.dispose();
    this._composer?.dispose?.();
    this._renderer?.dispose();
  }
}

customElements.define('model-3d-banner', Model3DBanner);
